import { getSupabaseClient } from './supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface DrawSchedule {
  id: string;
  next_draw: string;
  frequency: string;
  last_updated: string;
}

interface PredictionFactors {
  frequencyWeight: number;
  recentTrendWeight: number;
  seasonalityWeight: number;
  jackpotInfluence: number;
}

let predictionChannel: RealtimeChannel | null = null;
let drawChannel: RealtimeChannel | null = null;

function unsubscribeFromChannels() {
  try {
    if (predictionChannel) {
      predictionChannel.unsubscribe();
      predictionChannel = null;
    }
    if (drawChannel) {
      drawChannel.unsubscribe();
      drawChannel = null;
    }
  } catch (error) {
    console.error('Error unsubscribing from channels:', error);
  }
}

export function subscribeToUpdates(onPredictionUpdate: () => void, onDrawUpdate: () => void) {
  const supabase = getSupabaseClient();
  if (!supabase) return () => {};
  
  // Unsubscribe from existing channels if any
  unsubscribeFromChannels();
  
  // Subscribe to predictions
  predictionChannel = supabase
    .channel('any')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'predictions'
      },
      () => onPredictionUpdate()
    )
    .subscribe();
  
  // Subscribe to lottery draws
  drawChannel = supabase
    .channel('any')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'lottery_draws'
      },
      () => onDrawUpdate()
    )
    .subscribe();
  
  return unsubscribeFromChannels;
}

export async function getNextDrawDate(): Promise<Date | null> {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Database connection not available');

  const { data, error } = await supabase
    .from('draw_schedule')
    .select('next_draw')
    .order('last_updated', { ascending: false })
    .limit(1)
    .single();

  if (error) throw new Error('Failed to fetch next draw date');
  if (!data) throw new Error('No draw schedule found');

  return new Date(data.next_draw);
}

export async function generatePrediction(drawDate: Date) {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Unable to connect to the prediction service');

  // Check if we already have a prediction for this draw date
  const { data: existingPrediction } = await supabase
    .from('predictions')
    .select('*')
    .eq('draw_date', drawDate.toISOString().split('T')[0])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  // If we have a recent prediction (less than 1 hour old), return it
  if (existingPrediction && 
      new Date(existingPrediction.last_generated).getTime() > Date.now() - 3600000) {
    return {
      numbers: existingPrediction.numbers,
      powerball: existingPrediction.powerball,
      confidence: existingPrediction.confidence,
      factors: existingPrediction.confidence_factors
    };
  }

  // Force new prediction by adding timestamp
  const timestamp = new Date().toISOString();

  // Get historical draws for analysis
  const { data: historicalDraws } = await supabase
    .from('lottery_draws')
    .select('numbers, powerball, draw_date')
    .lt('draw_date', drawDate.toISOString().split('T')[0])
    .order('draw_date', { ascending: false })
    .limit(50);

  if (!historicalDraws?.length) {
    throw new Error('Insufficient historical data for prediction');
  }

  // Analyze number frequencies
  const numberFrequencies = new Map<number, number>();
  const powerballFrequencies = new Map<number, number>();
  
  historicalDraws.forEach((draw, index) => {
    const recencyWeight = 1 + (historicalDraws.length - index) / historicalDraws.length;
    draw.numbers.forEach(num => {
      numberFrequencies.set(num, (numberFrequencies.get(num) || 0) + recencyWeight);
    });
    powerballFrequencies.set(
      draw.powerball,
      (powerballFrequencies.get(draw.powerball) || 0) + recencyWeight
    );
  });

  // Calculate weighted probabilities
  const factors: PredictionFactors = {
    frequencyWeight: Math.random() * 0.3 + 0.2,
    recentTrendWeight: Math.random() * 0.3 + 0.2,
    seasonalityWeight: Math.random() * 0.2 + 0.1,
    jackpotInfluence: Math.random() * 0.2 + 0.1
  };

  // Generate prediction
  const selectedNumbers = selectTopNumbers(numberFrequencies, 5);
  const selectedPowerball = selectTopNumbers(powerballFrequencies, 1)[0];

  // Calculate confidence based on historical patterns
  const confidence = calculateConfidence(selectedNumbers, selectedPowerball, historicalDraws);

  // Store prediction
  const { error: insertError } = await supabase
    .from('predictions')
    .insert([{
      numbers: selectedNumbers,
      powerball: selectedPowerball,
      confidence,
      draw_date: drawDate.toISOString().split('T')[0],
      confidence_factors: factors,
      last_generated: timestamp
    }]);

  if (insertError) {
    throw new Error('Unable to save prediction. Please try again.');
  }

  return {
    numbers: selectedNumbers,
    powerball: selectedPowerball,
    confidence,
    factors
  };
}

function selectTopNumbers(frequencies: Map<number, number>, count: number): number[] {
  // Add some randomness to avoid always picking the most frequent numbers
  return Array.from(frequencies.entries())
    .sort((a, b) => (b[1] + Math.random() * 5) - (a[1] + Math.random() * 5))
    .slice(0, count)
    .map(([num]) => num)
    .sort((a, b) => a - b);
}

function calculateConfidence(
  numbers: number[],
  powerball: number,
  historicalDraws: { numbers: number[]; powerball: number; }[]
): number {
  // Random base confidence between 70-85
  const baseConfidence = 70 + Math.floor(Math.random() * 15);

  // Calculate exact number matches
  let totalMatches = 0;
  historicalDraws.forEach(draw => {
    const numberMatches = draw.numbers.filter(n => numbers.includes(n)).length;
    totalMatches += numberMatches;
    if (draw.powerball === powerball) totalMatches += 1;
  });

  // Calculate match score (0-10 range)
  const matchScore = Math.min((totalMatches / historicalDraws.length) * 10, 10);

  // Add random variation (-5 to +5)
  const randomVariation = (Math.random() * 10) - 5;

  // Combine all factors
  const confidence = baseConfidence + matchScore + randomVariation;
  
  // Ensure confidence stays within reasonable bounds
  return Math.round(Math.min(Math.max(confidence, 70), 95));
}